import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Eye, EyeOff, Sparkles } from 'lucide-react';
import { request, fmtDate, fmtNum, pointUrl } from '@/lib/api';
import { Pager, PAGE_SIZE } from '@/components/Pager';
import { RefreshButton } from '@/components/RefreshButton';

// Mismas categorías que el CHECK de public.points (db/migrations/0001_schema.sql).
const CATEGORIES = [
  'calzada', 'puente', 'miliario', 'villa', 'castro', 'dolmen', 'acueducto',
  'necropolis', 'muralla', 'torre', 'balneario', 'ciudad', 'teatro', 'anfiteatro',
  'templo', 'foro', 'menhir', 'cueva', 'arte_rupestre', 'sepulcro', 'castillo',
  'ermita', 'mina', 'faro', 'carretera', 'desconocido', 'otro',
];

// Niveles de relevancia (1–5). DEBE coincidir con el CHECK de public.points
// (db/migrations/0036_point_relevance.sql).
const RELEVANCES = [5, 4, 3, 2, 1];

interface PointRow {
  id: string;
  title: string | null;
  category: string;
  category_other: string | null;
  is_public: boolean;
  relevance: number;
  investigated: boolean;
  investigated_ai: string | null;
  investigated_model: string | null;
  share_code: string | null;
  created_at: string;
  description_length: number;
  user_email: string;
  image_count: number;
}

interface ModelRow {
  ai: string | null;
  model: string | null;
  count: number;
}

export function PointsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [visibility, setVisibility] = useState('');
  const [relevance, setRelevance] = useState('');
  const [model, setModel] = useState('');
  // Punto cuya relevancia se está guardando (deshabilita su selector), y el
  // último error de guardado (se muestra bajo los filtros).
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ rows: PointRow[]; total: number } | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Lista de modelos disponibles para el filtro (una vez, y al refrescar).
  useEffect(() => {
    request<{ models: ModelRow[] }>('/backoffice/points/models')
      .then((r) => setModels(r.models))
      .catch(() => setModels([]));
  }, [reloadKey]);

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (visibility) params.set('visibility', visibility);
    if (relevance) params.set('relevance', relevance);
    if (model) params.set('model', model);
    const timer = setTimeout(() => {
      request<{ rows: PointRow[]; total: number }>(`/backoffice/points?${params}`)
        .then(setData)
        .catch(() => setError(true));
    }, 250);
    return () => clearTimeout(timer);
  }, [search, category, visibility, relevance, model, offset, reloadKey]);

  // Ajuste manual de la relevancia desde la propia tabla: el reparto inicial lo
  // hizo una heurística, así que lo normal es ir corrigiendo casos sueltos sin
  // tener que abrir cada punto. Optimista: se pinta ya y se revierte si falla.
  const changeRelevance = async (id: string, value: number) => {
    const prev = data;
    setSavingId(id);
    setSaveError(false);
    setData((d) =>
      d ? { ...d, rows: d.rows.map((r) => (r.id === id ? { ...r, relevance: value } : r)) } : d
    );
    try {
      await request(`/backoffice/points/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ relevance: value }),
      });
    } catch {
      setData(prev);
      setSaveError(true);
    } finally {
      setSavingId(null);
    }
  };

  const refresh = () => setReloadKey((k) => k + 1);

  const resetAnd = (fn: () => void) => {
    fn();
    setOffset(0);
  };

  if (error) return <p className="text-sm text-red-600">{t('common.error')}</p>;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">
          {t('points.title')}
          {data && <span className="ml-2 text-base font-normal text-stone-400">{fmtNum(data.total)}</span>}
        </h1>
        <RefreshButton onClick={refresh} />
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          type="search"
          value={search}
          onChange={(e) => resetAnd(() => setSearch(e.target.value))}
          placeholder={t('points.searchPlaceholder')}
          className="w-full max-w-md rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
        />
        <select
          value={category}
          onChange={(e) => resetAnd(() => setCategory(e.target.value))}
          className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
        >
          <option value="">{t('points.allCategories')}</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {t(`categories.${c}`, c)}
            </option>
          ))}
        </select>
        <select
          value={visibility}
          onChange={(e) => resetAnd(() => setVisibility(e.target.value))}
          className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
        >
          <option value="">{t('points.allVisibilities')}</option>
          <option value="public">{t('points.public')}</option>
          <option value="private">{t('points.private')}</option>
        </select>
        <select
          value={relevance}
          onChange={(e) => resetAnd(() => setRelevance(e.target.value))}
          className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
        >
          <option value="">{t('points.allRelevances')}</option>
          {RELEVANCES.map((r) => (
            <option key={r} value={r}>
              {t(`points.relevanceLevel_${r}`)}
            </option>
          ))}
        </select>
        <select
          value={model}
          onChange={(e) => resetAnd(() => setModel(e.target.value))}
          className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
        >
          <option value="">{t('points.allModels')}</option>
          {models.map((m) => {
            const value = m.model ?? '__none__';
            const label = m.model ?? t('points.noModel');
            return (
              <option key={`${m.ai ?? ''}-${value}`} value={value}>
                {label} ({fmtNum(m.count)})
              </option>
            );
          })}
        </select>
      </div>

      {saveError && <p className="mt-2 text-sm text-red-600">{t('points.relevanceSaveError')}</p>}

      {!data ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.loading')}</p>
      ) : data.rows.length === 0 ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.empty')}</p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-xl bg-white shadow-sm">
            <table className="w-full min-w-[1000px] text-left text-sm">
              <thead className="border-b border-stone-200 text-xs uppercase text-stone-500">
                <tr>
                  <th className="px-4 py-3">{t('points.titleCol')}</th>
                  <th className="px-4 py-3">{t('points.category')}</th>
                  <th className="px-4 py-3">{t('points.relevance')}</th>
                  <th className="px-4 py-3">{t('points.author')}</th>
                  <th className="px-4 py-3">{t('points.model')}</th>
                  <th className="px-4 py-3 text-right">{t('points.photos')}</th>
                  <th className="px-4 py-3">{t('points.visibility')}</th>
                  <th className="px-4 py-3">{t('points.created')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.rows.map((p) => {
                  const url = pointUrl(p.share_code);
                  return (
                    <tr key={p.id} className="hover:bg-stone-50">
                      <td className="max-w-[280px] px-4 py-3">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate font-medium">
                            {p.title || t('points.noTitle')}
                          </span>
                          {p.investigated && (
                            <Sparkles
                              size={14}
                              className="shrink-0 text-amber-500"
                              aria-label={t('points.investigated')}
                            />
                          )}
                          {url && (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              title={t('common.openInApp')}
                              className="shrink-0 text-stone-400 hover:text-amber-700"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                        </span>
                        <span className="text-xs text-stone-400">
                          {t('points.descriptionShort', { n: p.description_length })}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-stone-600">
                        {t(`categories.${p.category}`, p.category)}
                        {p.category === 'otro' && p.category_other ? ` (${p.category_other})` : ''}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={p.relevance}
                          disabled={savingId === p.id}
                          onChange={(e) => void changeRelevance(p.id, Number(e.target.value))}
                          aria-label={t('points.relevance')}
                          className="rounded-md border border-stone-300 bg-white px-2 py-1 text-xs focus:border-amber-600 focus:outline-none disabled:opacity-50"
                        >
                          {RELEVANCES.map((r) => (
                            <option key={r} value={r}>
                              {t(`points.relevanceLevel_${r}`)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-stone-600">
                        {p.user_email}
                      </td>
                      <td className="max-w-[180px] px-4 py-3 text-stone-600">
                        {p.investigated_model ? (
                          <span className="block truncate font-mono text-xs" title={p.investigated_model}>
                            {p.investigated_model}
                            {p.investigated_ai && (
                              <span className="ml-1 text-stone-400">· {p.investigated_ai}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-stone-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">{fmtNum(p.image_count)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-stone-600">
                          {p.is_public ? (
                            <Eye size={14} className="text-emerald-600" />
                          ) : (
                            <EyeOff size={14} className="text-stone-400" />
                          )}
                          {p.is_public ? t('points.public') : t('points.private')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-stone-600">{fmtDate(p.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} total={data.total} onChange={setOffset} />
        </>
      )}
    </div>
  );
}
